import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { BarChartOutlined } from '@mui/icons-material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { TABLE_FONT_FAMILY } from '../../theme';
import { CommercialDashboard, buildStageBars, formatEuro } from '../../utils/commercialDashboard';
import { ConsolePanel, EmptyNote, PanelStat } from './ConsolePanel';
import { STAGE_RAMP, useConsolePalette } from './consolePalette';

type StageValuePanelProps = {
  dashboard: CommercialDashboard;
};

const StageValuePanel: React.FC<StageValuePanelProps> = ({ dashboard }) => {
  const palette = useConsolePalette();
  const bars = useMemo(() => buildStageBars(dashboard.funnel), [dashboard.funnel]);
  const hasValue = bars.some((bar) => bar.value > 0);

  return (
    <ConsolePanel
      title="Pipeline by stage"
      subtitle={`Unweighted value of open leads · Won closed in ${dashboard.period.label}`}
      icon={<BarChartOutlined />}
      action={(
        <>
          <PanelStat label="Total pipeline" value={formatEuro(dashboard.kpis.pipeline_value.value)} />
          <PanelStat label="Forecast" value={formatEuro(dashboard.kpis.pipeline_forecast.value)} />
        </>
      )}
    >
      {hasValue ? (
        <Box sx={{ height: 252 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart layout="vertical" data={bars} margin={{ top: 4, right: 56, left: 4, bottom: 0 }}>
              <CartesianGrid horizontal={false} stroke={palette.rule} />
              <XAxis
                type="number"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 11, fill: palette.inkFaint, fontFamily: TABLE_FONT_FAMILY }}
                tickFormatter={(value: number) => formatEuro(value)}
              />
              <YAxis
                type="category"
                dataKey="label"
                width={96}
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: palette.inkSoft }}
              />
              <RechartsTooltip
                cursor={{ fill: palette.hover }}
                contentStyle={{
                  borderRadius: 4,
                  border: `1px solid ${palette.border}`,
                  boxShadow: 'none',
                  backgroundColor: palette.surface,
                  fontFamily: TABLE_FONT_FAMILY,
                  fontSize: 12,
                }}
                labelStyle={{ color: palette.ink, fontWeight: 600 }}
                itemStyle={{ color: palette.ink }}
                formatter={(value) => [formatEuro(Number(value) || 0), 'Value']}
              />
              <Bar dataKey="value" barSize={14} radius={[0, 2, 2, 0]} isAnimationActive={false}>
                {bars.map((bar) => (
                  <Cell key={bar.status} fill={STAGE_RAMP[bar.status]} />
                ))}
                <LabelList
                  dataKey="value"
                  position="right"
                  fill={palette.inkSoft}
                  fontSize={11}
                  fontFamily={TABLE_FONT_FAMILY}
                  formatter={(value: unknown) => (Number(value) ? formatEuro(Number(value)) : '')}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      ) : (
        <EmptyNote>No value is recorded on open leads yet. Add an estimated or proposal amount to a lead to chart it here.</EmptyNote>
      )}
    </ConsolePanel>
  );
};

export default StageValuePanel;
